import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import {
  Email as MailIcon,
  Event as CalendarIcon,
  TableChart as SheetsIcon,
  Description as DocsIcon,
  CheckCircle,
  Cancel as XCircleIcon,
} from '@mui/icons-material';

const API_BASE = import.meta.env.VITE_API_URL || '';
const CUSTOMER_ID = 'RlUOcxU7HBZ95uoHyxHLJXrA6jx2'; // Replace with actual customer ID from auth

interface ConnectionStatus {
  is_connected: boolean;
  google_email?: string;
  enabled_services: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function GoogleWorkspaceIntegration() {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Email form
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailResult, setEmailResult] = useState<any>(null);
  
  // Calendar form
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingEmail, setMeetingEmail] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingDescription, setMeetingDescription] = useState('');
  const [calendarResult, setCalendarResult] = useState<any>(null);
  
  // Sheets form
  const [sheetsDealName, setSheetsDealName] = useState('');
  const [sheetsResult, setSheetsResult] = useState<any>(null);
  
  // Docs form
  const [docCompanyName, setDocCompanyName] = useState('');
  const [docSummary, setDocSummary] = useState('');
  const [docsResult, setDocsResult] = useState<any>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/google-workspace/status/${CUSTOMER_ID}`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        // API not available, show demo mode
        setStatus({ is_connected: false, enabled_services: [] });
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
      // Show demo mode if API unavailable
      setStatus({ is_connected: false, enabled_services: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleDraftEmail = async () => {
    try {
      setLoading(true);
      setEmailResult(null);
      const response = await fetch(`${API_BASE}/api/google-workspace/draft-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          to_email: emailTo,
          subject: emailSubject,
          body: emailBody
        })
      });
      const data = await response.json();
      setEmailResult(data);
    } catch (error) {
      setEmailResult({ success: false, error: String(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMeeting = async () => {
    try {
      setLoading(true);
      setCalendarResult(null);
      
      // Combine date and time
      const startTime = new Date(`${meetingDate}T${meetingTime}`).toISOString();
      const endTime = new Date(new Date(startTime).getTime() + 3600000).toISOString(); // +1 hour
      
      const response = await fetch(`${API_BASE}/api/google-workspace/schedule-meeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          prospect_email: meetingEmail,
          meeting_title: meetingTitle,
          start_time: startTime,
          end_time: endTime,
          description: meetingDescription
        })
      });
      const data = await response.json();
      setCalendarResult(data);
    } catch (error) {
      setCalendarResult({ success: false, error: String(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleExportSheets = async () => {
    try {
      setLoading(true);
      setSheetsResult(null);
      const response = await fetch(`${API_BASE}/api/google-workspace/export-sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          deal_data: {
            pipeline_name: sheetsDealName || 'CRM Export',
            deals: [
              { name: 'Sample Deal 1', stage: 'Negotiation', value: 50000, close_date: '2025-12-15', probability: 75 },
              { name: 'Sample Deal 2', stage: 'Proposal', value: 25000, close_date: '2025-12-10', probability: 60 }
            ]
          }
        })
      });
      const data = await response.json();
      setSheetsResult(data);
    } catch (error) {
      setSheetsResult({ success: false, error: String(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDoc = async () => {
    try {
      setLoading(true);
      setDocsResult(null);
      const response = await fetch(`${API_BASE}/api/google-workspace/create-doc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          plan_data: {
            title: `${docCompanyName} - Business Plan`,
            company_name: docCompanyName,
            executive_summary: docSummary,
            market_analysis: 'Market growing at 15% CAGR...',
            financial_projections: 'Year 1: $100K, Year 2: $250K, Year 3: $500K'
          }
        })
      });
      const data = await response.json();
      setDocsResult(data);
    } catch (error) {
      setDocsResult({ success: false, error: String(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Google Workspace Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {status?.is_connected 
              ? `Connected as ${status.google_email}` 
              : 'Not connected'}
          </Typography>
          
          {status?.is_connected ? (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircle color="success" />
                <Typography fontWeight="medium">Connected</Typography>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {status.enabled_services.map(service => (
                  <Chip key={service} label={service} size="small" />
                ))}
              </Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <XCircleIcon color="error" />
              <Typography>Not connected. Connect via Landing Page Portal.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tabs for connected users */}
      {status?.is_connected && (
        <>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="workspace tabs" sx={{ mb: 3 }}>
            <Tab icon={<MailIcon />} label="Email" />
            <Tab icon={<CalendarIcon />} label="Calendar" />
            <Tab icon={<SheetsIcon />} label="Sheets" />
            <Tab icon={<DocsIcon />} label="Docs" />
          </Tabs>

          {/* Email Tab */}
          <TabPanel value={activeTab} index={0}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Draft Email</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Create a draft email in Gmail
                </Typography>
                
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="To"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="prospect@company.com"
                    fullWidth
                  />
                  <TextField
                    label="Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Partnership Opportunity"
                    fullWidth
                  />
                  <TextField
                    label="Message"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Hi there..."
                    multiline
                    rows={6}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleDraftEmail}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <MailIcon />}
                  >
                    Draft Email
                  </Button>
                  
                  {emailResult && (
                    <Alert severity={emailResult.success ? "success" : "error"}>
                      {emailResult.success ? (
                        <>
                          <Typography fontWeight="medium">Draft created!</Typography>
                          <Typography variant="body2">Draft ID: {emailResult.draft_id}</Typography>
                          <Link
                            href="https://mail.google.com/mail/#drafts"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View in Gmail
                          </Link>
                        </>
                      ) : (
                        <Typography>{emailResult.error}</Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Calendar Tab */}
          <TabPanel value={activeTab} index={1}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Schedule Meeting</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Create a meeting in Google Calendar
                </Typography>
                
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Meeting Title"
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    placeholder="Demo Call"
                    fullWidth
                  />
                  <TextField
                    label="Attendee Email"
                    value={meetingEmail}
                    onChange={(e) => setMeetingEmail(e.target.value)}
                    placeholder="prospect@company.com"
                    fullWidth
                  />
                  <Box display="flex" gap={2}>
                    <TextField
                      label="Date"
                      type="date"
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Time"
                      type="time"
                      value={meetingTime}
                      onChange={(e) => setMeetingTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Box>
                  <TextField
                    label="Description"
                    value={meetingDescription}
                    onChange={(e) => setMeetingDescription(e.target.value)}
                    placeholder="Discussion topics..."
                    multiline
                    rows={4}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleScheduleMeeting}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CalendarIcon />}
                  >
                    Schedule Meeting
                  </Button>
                  
                  {calendarResult && (
                    <Alert severity={calendarResult.success ? "success" : "error"}>
                      {calendarResult.success ? (
                        <>
                          <Typography fontWeight="medium">Meeting scheduled!</Typography>
                          <Typography variant="body2">Event ID: {calendarResult.event_id}</Typography>
                          <Link
                            href="https://calendar.google.com"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View in Calendar
                          </Link>
                        </>
                      ) : (
                        <Typography>{calendarResult.error}</Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Sheets Tab */}
          <TabPanel value={activeTab} index={2}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Export to Sheets</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Export deal pipeline to Google Sheets
                </Typography>
                
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Pipeline Name"
                    value={sheetsDealName}
                    onChange={(e) => setSheetsDealName(e.target.value)}
                    placeholder="Q4 2024 Pipeline"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleExportSheets}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SheetsIcon />}
                  >
                    Export to Sheets
                  </Button>
                  
                  {sheetsResult && (
                    <Alert severity={sheetsResult.success ? "success" : "error"}>
                      {sheetsResult.success ? (
                        <>
                          <Typography fontWeight="medium">Exported successfully!</Typography>
                          <Link
                            href={sheetsResult.spreadsheet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Spreadsheet
                          </Link>
                        </>
                      ) : (
                        <Typography>{sheetsResult.error}</Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Docs Tab */}
          <TabPanel value={activeTab} index={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Create Business Plan</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Generate a business plan document in Google Docs
                </Typography>
                
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Company Name"
                    value={docCompanyName}
                    onChange={(e) => setDocCompanyName(e.target.value)}
                    placeholder="Acme Corp"
                    fullWidth
                  />
                  <TextField
                    label="Executive Summary"
                    value={docSummary}
                    onChange={(e) => setDocSummary(e.target.value)}
                    placeholder="Brief overview of the business..."
                    multiline
                    rows={4}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleCreateDoc}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <DocsIcon />}
                  >
                    Create Document
                  </Button>
                  
                  {docsResult && (
                    <Alert severity={docsResult.success ? "success" : "error"}>
                      {docsResult.success ? (
                        <>
                          <Typography fontWeight="medium">Document created!</Typography>
                          <Link
                            href={docsResult.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Document
                          </Link>
                        </>
                      ) : (
                        <Typography>{docsResult.error}</Typography>
                      )}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </TabPanel>
        </>
      )}
    </Box>
  );
}

export default GoogleWorkspaceIntegration;
